import React, { useEffect, useState } from 'react';
import { InputBase, Link, Typography } from '@mui/material';
import TouchTooltip from '../../common/TouchTooltip';
import { N_DEFAULT, N_MAX, N_MIN, SOLD_OUT_AT_TOOLTIP } from './constants';
import { VIEW_DESCRIPTIONS, bandLabels, bucketRange, fmtInt } from '../../../utils/buyerInsightsFormat';

// A small inline whole-number field (Daniel 2026-10-14): applied after a short pause, or on blur /
// Enter; an out-of-range value snaps back. `allowEmpty` lets the field be cleared (a cut point
// removed), which applies null.
const APPLY_DELAY_MS = 600;

export const NumberField = ({ value, onChange, min, max, ariaLabel, allowEmpty = false, width = '4ch' }) => {
  const asText = (v) => (v === null || v === undefined ? '' : String(v));
  const [text, setText] = useState(asText(value));
  useEffect(() => setText(asText(value)), [value]);
  const parse = (t) => {
    if (t === '') return allowEmpty ? null : undefined;
    const v = Number(t);
    return Number.isInteger(v) && v >= min && v <= max ? v : undefined;
  };
  useEffect(() => {
    const v = parse(text);
    if (v === undefined || v === value) return undefined;
    const t = setTimeout(() => onChange(v), APPLY_DELAY_MS);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <InputBase
      value={text}
      onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
      onBlur={() => {
        const v = parse(text);
        if (v === undefined) setText(asText(value));
        else if (v !== value) onChange(v);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      inputProps={{ inputMode: 'numeric', 'aria-label': ariaLabel, min, max }}
      sx={{
        fontSize: 'inherit',
        fontWeight: 600,
        color: 'text.primary',
        width,
        borderBottom: 1,
        borderColor: 'divider',
        '& input': { p: 0, textAlign: 'center' },
      }}
    />
  );
};

const DaysField = ({ n, onNChange }) => (
  <NumberField value={n} onChange={onNChange} min={N_MIN} max={N_MAX} ariaLabel="Minimum days sold out" />
);

// The Mixpanel-style breakdown of the still-wanted sizes by the price they sold out at (sold-out-at
// spec §3, §4): "Sold out at: full price 2,166 · below 20% off 0 · … · unknown 115 sizes". Every
// bucket is a link that sets the filter; unknown is text (nobody buys on sell-outs we did not see).
export const SoldOutAtBreakdown = ({ breakdown, onSelect }) => {
  if (!breakdown) return null;
  const bands = breakdown.bands || [];
  const parts = [{ label: 'full price', count: breakdown.full_price, soldOutAt: 'full_price', offRange: null }];
  if (bands.length) {
    bandLabels(bands).forEach((label, i) =>
      parts.push({ label, count: breakdown.buckets?.[i] ?? 0, soldOutAt: 'discounted', offRange: bucketRange(i, bands) })
    );
  } else {
    parts.push({ label: 'discounted', count: breakdown.discounted, soldOutAt: 'discounted', offRange: null });
  }
  return (
    <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="sold-out-at-breakdown">
      <span>Sold out at: </span>
      {parts.map((p, i) => (
        <React.Fragment key={p.label}>
          {i > 0 && <span> · </span>}
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => onSelect && onSelect({ soldOutAt: p.soldOutAt, offRange: p.offRange, label: p.label })}
          >
            {`${p.label} ${fmtInt(p.count)}`}
          </Link>
        </React.Fragment>
      ))}
      <span> · </span>
      <TouchTooltip title={SOLD_OUT_AT_TOOLTIP}>
        <span style={{ cursor: 'help' }}>{`unknown ${fmtInt(breakdown.unknown)}`}</span>
      </TouchTooltip>
      <span> sizes</span>
    </Typography>
  );
};

const ViewDescription = ({ view, n = N_DEFAULT, onNChange, breakdown, onBreakdownSelect }) => {
  if (view === 'still_wanted') {
    return (
      <>
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
        >
          <span>Sold out for</span>
          <DaysField n={n} onNChange={onNChange} />
          <span>days or more in this period. Shoppers continued to show interest.</span>
        </Typography>
        <SoldOutAtBreakdown breakdown={breakdown} onSelect={onBreakdownSelect} />
      </>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {VIEW_DESCRIPTIONS[view]}
    </Typography>
  );
};

export default ViewDescription;
